import Vue from "vue";
import moment from "moment";
import firebase from "firebase/app";
import "firebase/firestore";
import { FeedbackFamilyHubUser, FeedbackRequest, normalizeFeedbackType } from '@/models/FeedbackModels';
import { hasAccess } from '@/utils/accessControl/accessControl';
import { AccessMatrixKeys } from '@/utils/accessControl/accessMatrixKeysEnum';

export default Vue.extend({
  name: 'FamilyFeedbackRequests',
  
  data() {
    return {
      loading: false,
      search: "",
      feedbackRequests: [] as FeedbackRequest[]
    };
  },

  computed: {
    facilityId(): string {
      return this.$store.state.authModule.currentFacility;
    },

    currentUser() {
      return this.$store.state.authModule.user;
    },

    canViewFamilyFeedback() {
      return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.FamilyFeedback_ViewList);
    },

    headers() {
      return [
        { text: 'Resident', value: 'residentName', sortable: true },
        { text: 'Family Member', value: 'familyMemberName', sortable: true },
        { text: 'Data Submitted', value: 'createdAt', sortable: true },
        { text: 'Time Since Last Update', value: 'timeSinceLastUpdateHours', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Reference', value: 'referenceId', sortable: true },
        { text: 'Type', value: 'type', sortable: true },
        { text: 'Subject', value: 'subject', sortable: true },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ];
    },

    filteredFeedbackRequests(): FeedbackRequest[] {
      let filtered = this.feedbackRequests;

      if (this.search) {
        const query = this.search.toLowerCase();
        // Prioritize status when searching for 'new'
        if (query === 'new') {
          filtered = filtered.filter(req => req.status.toLowerCase() === 'new');
        } else {
          filtered = filtered.filter(req => 
            // Search in resident name and room
            req.residentName.toLowerCase().includes(query) ||
            req.residentRoom.toLowerCase().includes(query) ||
            // Search in family member name and relation
            req.familyMemberName.toLowerCase().includes(query) ||
            req.familyMemberRelation.toLowerCase().includes(query) ||
            // Search in date submitted (format it first)
            this.formatDate(req.createdAt).toLowerCase().includes(query) ||
            // Search in time since last update
            (req.timeSinceLastUpdate || '').toLowerCase().includes(query) ||
            // Search in status
            req.status.toLowerCase().includes(query) ||
            // Search in reference
            req.referenceId.toLowerCase().includes(query) ||
            // Search in feedback type
            req.type.toLowerCase().includes(query) ||
            // Search in subject
            req.subject.toLowerCase().includes(query) ||
            // Search in feedback details
            req.details.toLowerCase().includes(query)
          );
        }
      }

      return filtered;
    }
  },

  async created() {
    // Check if user has permission to access this page
    if (!this.canViewFamilyFeedback) {
      this.$toast.open({
        message: 'You do not have permission to access Family Feedback Requests',
        type: 'error'
      });
      this.$router.push('/dashboard');
      return;
    }
    
    await this.loadData();
  },

  methods: {
    /**
     * Calculate time since last update based on:
     * - Time since request created
     * - Time since status updated (updatedAt)
     * - Time since last staff reply
     * - Time since last family reply
     * Returns the most recent timestamp
     */
    calculateTimeSinceLastUpdate(
      createdAt: Date,
      updatedAt: Date | undefined,
      lastStaffReplyAt: Date | undefined,
      lastFamilyReplyAt: Date | undefined
    ): { display: string; color: string; hours: number } {
      // Gather all relevant timestamps
      const timestamps = [createdAt];
      if (updatedAt) timestamps.push(updatedAt);
      if (lastStaffReplyAt) timestamps.push(lastStaffReplyAt);
      if (lastFamilyReplyAt) timestamps.push(lastFamilyReplyAt);

      // Find the most recent timestamp
      const lastUpdate = timestamps.reduce((latest, current) => {
        return current > latest ? current : latest;
      });

      // Calculate hours difference
      const now = moment();
      const lastUpdateMoment = moment(lastUpdate);
      const hoursDiff = now.diff(lastUpdateMoment, 'hours');
      const daysDiff = now.diff(lastUpdateMoment, 'days');

      let display = '';
      let color = '';

      if (hoursDiff < 72) {
        // Less than 72 hours - green, display in hours or days
        if (hoursDiff < 24) {
          display = hoursDiff === 1 ? '1 Hour' : `${hoursDiff} Hours`;
        } else {
          display = daysDiff === 1 ? '1 Day' : `${daysDiff} Days`;
        }
        color = 'green';
      } else if (hoursDiff < 168) {
        // 72 hours to 7 days - orange, display in days
        display = `${daysDiff} Days`;
        color = 'orange';
      } else {
        // 7 days or more - red, display in days
        display = `${daysDiff} Days`;
        color = 'red';
      }

      return { display, color, hours: hoursDiff };
    },

    async loadData() {
      this.loading = true;
      try {
        await this.loadFeedbackRequests();
      } catch (error) {
        console.error("Error loading feedback requests:", error);
        this.$toast.open({
          message: this.$t('GeneralTranslations.UnhandledError') as string,
          type: "error"
        });
      } finally {
        this.loading = false;
      }
    },

    async loadFeedbackRequests() {
      try {
        console.log('Loading feedback requests for facilityId:', this.facilityId);
        
        // Query without orderBy first to check if index is needed
        const query = firebase
          .firestore()
          .collection("familyFeedback")
          .where("facilityId", "==", this.facilityId);
        
        // Try to add orderBy, but if it fails due to missing index, query without it
        let querySnapshot;
        try {
          querySnapshot = await query.orderBy("createdAt", "desc").get();
        } catch (indexError) {
          console.warn('Index not found, querying without orderBy:', indexError);
          querySnapshot = await query.get();
        }

        console.log('Found feedback requests:', querySnapshot.size);

        // Fetch all resident and family member data in parallel
        const feedbackPromises = querySnapshot.docs.map(async doc => {
          const data = doc.data();
          console.log('Processing feedback:', doc.id, data);
          
          // Fetch resident data to get room name
          let residentRoom = '';
          let residentName = data.residentName || 'Unknown';
          if (data.residentId) {
            try {
              const residentDoc = await firebase
                .firestore()
                .collection("residents")
                .doc(data.residentId)
                .get();
              
              if (residentDoc.exists) {
                const residentData = residentDoc.data();
                residentRoom = residentData?.roomName || '';
                residentName = residentData?.name || residentName;
              }
            } catch (err) {
              console.error(`Error fetching resident ${data.residentId}:`, err);
            }
          }

          // Fetch family member relationship
          let familyMemberRelation = '';
          if (data.residentId && data.familyMemberId) {
            try {
              const residentDoc = await firebase
                .firestore()
                .collection("residents")
                .doc(data.residentId)
                .get();
              
              if (residentDoc.exists) {
                const residentData = residentDoc.data();
                const familyHubUser = residentData?.familyHubUsers?.find(
                  (user: FeedbackFamilyHubUser) => user.id === data.familyMemberId
                );
                familyMemberRelation = familyHubUser?.relationship || '';
              }
            } catch (err) {
              console.error(`Error fetching family member relationship:`, err);
            }
          }

          // Fetch messages to find last reply times
          let lastStaffReplyAt: Date | undefined;
          let lastFamilyReplyAt: Date | undefined;
          
          try {
            const messagesSnapshot = await firebase
              .firestore()
              .collection("familyFeedbackMessages")
              .where("feedbackId", "==", doc.id)
              .where("facilityId", "==", this.facilityId)
              .orderBy("createdAt", "desc")
              .limit(50) // Limit to recent messages for performance
              .get();

            messagesSnapshot.docs.forEach(messageDoc => {
              const messageData = messageDoc.data();
              const messageCreatedAt = messageData.createdAt?.toDate() || new Date();
              
              if (messageData.senderRole === 'staff' && !lastStaffReplyAt) {
                lastStaffReplyAt = messageCreatedAt;
              } else if (messageData.senderRole === 'family' && !lastFamilyReplyAt) {
                lastFamilyReplyAt = messageCreatedAt;
              }
            });
          } catch (err) {
            console.error(`Error fetching messages for feedback ${doc.id}:`, err);
          }

          // Calculate time since last update
          const createdAt = data.createdAt?.toDate() || new Date();
          const updatedAt = data.updatedAt?.toDate();
          const timeInfo = this.calculateTimeSinceLastUpdate(
            createdAt,
            updatedAt,
            lastStaffReplyAt,
            lastFamilyReplyAt
          );

          return {
            id: doc.id,
            residentId: data.residentId || '',
            residentName: residentName,
            residentRoom: residentRoom,
            familyMemberId: data.familyMemberId || '',
            familyMemberName: data.familyMemberName || 'Unknown',
            familyMemberRelation: familyMemberRelation,
            status: data.status || 'New',
            referenceId: data.referenceId || '',
            type: normalizeFeedbackType(data.type),
            subject: data.subject || '',
            details: data.details || '',
            createdAt: createdAt,
            updatedAt: updatedAt,
            commentCount: (data.unreadMessagesFromFamilyMember || 0) + (data.unreadMessagesFromStaff || 0),
            careNote: data.careNote,
            channel: data.channel,
            resolution: data.resolution,
            lastStaffReplyAt,
            lastFamilyReplyAt,
            timeSinceLastUpdate: timeInfo.display,
            timeSinceLastUpdateColor: timeInfo.color,
            timeSinceLastUpdateHours: timeInfo.hours
          };
        });

        this.feedbackRequests = await Promise.all(feedbackPromises);
        
        // Sort by date in JavaScript if we couldn't use Firestore orderBy
        this.feedbackRequests.sort((a, b) => {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        
        console.log('Loaded feedback requests:', this.feedbackRequests.length);
      } catch (error) {
        console.error("Error loading feedback requests from Firestore:", error);
        // Fallback to empty array if there's an error
        this.feedbackRequests = [];
      }
    },

    formatDate(date: Date | string) {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY');
    },

    getStatusColor(status: string) {
      const colors: Record<string, string> = {
        'New': 'info',
        'Open': 'success',
        'Pending': 'warning',
        'Closed': 'grey'
      };
      return colors[status] || 'grey';
    },

    customSort(items: FeedbackRequest[], sortBy: string[], sortDesc: boolean[]) {
      if (sortBy.length === 0) return items;

      const sortKey = sortBy[0];
      const isDescending = sortDesc[0];

      return items.sort((a, b) => {
        let aVal = a[sortKey];
        let bVal = b[sortKey];

        // Special handling for timeSinceLastUpdateHours to ensure numeric comparison
        if (sortKey === 'timeSinceLastUpdateHours') {
          aVal = a.timeSinceLastUpdateHours || 0;
          bVal = b.timeSinceLastUpdateHours || 0;
          return isDescending ? bVal - aVal : aVal - bVal;
        }

        // Special handling for dates
        if (sortKey === 'createdAt') {
          const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return isDescending ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
        }

        // Default string comparison for other fields
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return isDescending 
            ? bVal.localeCompare(aVal) 
            : aVal.localeCompare(bVal);
        }

        // Numeric comparison
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return isDescending ? bVal - aVal : aVal - bVal;
        }

        return 0;
      });
    },

    handleRowClick(item: FeedbackRequest) {
      this.viewFeedbackThread(item.id);
    },

    viewFeedbackThread(feedbackId: string) {
      this.$router.push(`/family-feedback-requests/${feedbackId}`);
    }
  }
});
